import { useCallback, useEffect, useMemo, useState } from "react"
import { TextSelection } from "@tiptap/pm/state"

import type { Node as ProseMirrorNode } from "@tiptap/pm/model"
import type { NodeViewProps } from "@tiptap/react"

export interface PanelChild {
	key: string
	label: string
}

interface PanelGroupArgs extends Pick<NodeViewProps, "node" | "editor"> {
	getPos: () => number | undefined
	label: (child: ProseMirrorNode, index: number) => string
}

export interface PanelGroup {
	panels: PanelChild[]
	active: number
	select: (index: number) => void
	rename: (index: number, title: string) => void
	positionOf: (index: number) => number | undefined
}

// Which panel shows is view state, so it stays out of the document and never
// travels through collaboration or a version.
export function usePanelGroup({
	node,
	editor,
	getPos,
	label,
}: PanelGroupArgs): PanelGroup {
	const [active, setActive] = useState(1)

	const panels = useMemo(() => {
		const children: PanelChild[] = []
		node.forEach((child, _, index) => {
			children.push({
				key: `${index}-${label(child, index)}`,
				label: label(child, index),
			})
		})
		return children
	}, [node, label])

	// Read on use, never cached: editing anything above the block moves every
	// position inside it while this node object stays the same.
	const positionOf = useCallback(
		(index: number) => {
			const base = getPos()
			if (base === undefined) return undefined
			let offset = base + 1
			let found: number | undefined
			node.forEach((child, _, childIndex) => {
				if (childIndex === index - 1) found = offset
				offset += child.nodeSize
			})
			return found
		},
		[getPos, node],
	)

	// Typing inside a panel has to keep that panel on screen, so the caret
	// drives the selected label rather than the other way round.
	useEffect(() => {
		const sync = () => {
			const base = getPos()
			if (base === undefined) return
			const head = editor.state.selection.head
			if (head <= base || head >= base + node.nodeSize) return
			let offset = base + 1
			node.forEach((child, _, index) => {
				if (head >= offset && head <= offset + child.nodeSize) {
					setActive(index + 1)
				}
				offset += child.nodeSize
			})
		}
		sync()
		editor.on("selectionUpdate", sync)
		return () => {
			editor.off("selectionUpdate", sync)
		}
	}, [editor, node, getPos])

	const select = useCallback(
		(index: number) => {
			setActive(index)
			const pos = positionOf(index)
			if (pos === undefined) return
			// Biased forward: the panel's own first text position, not the one
			// before it, which sits in the panel above.
			editor.commands.command(({ tr, dispatch }) => {
				if (dispatch) {
					tr.setSelection(TextSelection.near(tr.doc.resolve(pos + 1), 1))
				}
				return true
			})
		},
		[editor, positionOf],
	)

	const rename = useCallback(
		(index: number, title: string) => {
			const pos = positionOf(index)
			if (pos === undefined) return
			editor
				.chain()
				.command(({ tr }) => {
					tr.setNodeAttribute(pos, "title", title.trim() || null)
					return true
				})
				.run()
		},
		[editor, positionOf],
	)

	return { panels, active, select, rename, positionOf }
}
